import { UserGroupSummary } from '@admin/api/userGroups';
import { useUserGroupsQuery } from '@admin/hooks/useUserGroups';
import AutocompleteJs from '@common/components/autocomplete/Autocomplete';
import WaveSelectFieldJs from '@common/components/form/WaveSelectField';
import WaveTextFieldJs from '@common/components/form/WaveTextField';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React, { useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import UserRolesJs from './UserRoles';

// These are untyped JS components, so give them a loose type until they're
// converted to TSX.
const Autocomplete = AutocompleteJs as React.ComponentType<any>;
const UserRoles = UserRolesJs as React.ComponentType<any>;
const WaveSelectField = WaveSelectFieldJs as React.ComponentType<any>;
const WaveTextField = WaveTextFieldJs as React.ComponentType<any>;

const useStyles = makeStyles()((theme) => ({
  userFormContainer: {
    padding: theme.typography.pxToRem(25)
  }
}));

type OptionMap = Record<string, { label?: string; name?: string; value?: unknown }>;

// NOTE: Only the fields this form reads or writes. The user object is passed
// back to UserManage whole, so any other attributes are carried through.
interface FormUser {
  [key: string]: unknown;
  is_division_manager?: boolean;
  is_department_manager?: boolean;
  is_educator?: boolean;
  is_guest?: boolean;
  is_supervisor?: boolean;
  supervisor_id?: number | null;
  supervisor_2_id?: number | null;
  supervisor?: { full_name: string } | null;
  supervisor_2?: { full_name: string } | null;
  // Returned by the users endpoints; the form sends back user_group_ids.
  user_groups?: UserGroupSummary[];
  user_group_ids?: number[];
}

interface FormInstitute {
  custom_user_field_1?: string;
  custom_user_field_1_enabled?: boolean;
  custom_user_field_2?: string;
  custom_user_field_2_enabled?: boolean;
}

interface UserFormProps {
  user: FormUser | null;
  institute: FormInstitute;
  departmentFields: OptionMap;
  divisionFields: OptionMap;
  employmentTypeFields: OptionMap;
  validationErrors: Record<string, string[]>;
  onUserChange: (user: FormUser) => void;
  // Only admins can read and assign user groups.
  canEditUserGroups?: boolean;
}

const SUPERVISOR_ROLES = ['supervisor', 'department_manager', 'division_manager'];

const UserForm = ({
  user,
  institute,
  departmentFields,
  divisionFields,
  employmentTypeFields,
  validationErrors,
  onUserChange,
  canEditUserGroups = false
}: UserFormProps) => {
  const { classes } = useStyles();
  const [supervisorLabel, setSupervisorLabel] = useState(user?.supervisor?.full_name);
  const [supervisor2Label, setSupervisor2Label] = useState(user?.supervisor_2?.full_name);
  const { data: userGroups = [] } = useUserGroupsQuery({ enabled: canEditUserGroups });

  // WaveSelectField takes a map of options. Using the numeric id as the value
  // keeps user_group_ids as numbers rather than the map's string keys. The
  // user's own groups are included so they show as selected while the
  // institute's group list loads.
  const userGroupFields = useMemo<OptionMap>(
    () =>
      Object.fromEntries(
        [...(user?.user_groups ?? []), ...userGroups].map((group) => [
          group.id,
          { label: group.name, value: group.id }
        ])
      ),
    [user?.user_groups, userGroups]
  );

  if (!user) {
    return <div>No User</div>;
  }

  const hasSupervisor =
    user.is_educator ||
    user.is_guest ||
    user.is_supervisor ||
    user.is_division_manager ||
    user.is_department_manager;

  const errorStatus = (field: string) =>
    Object.prototype.hasOwnProperty.call(validationErrors, field) ||
    Object.prototype.hasOwnProperty.call(validationErrors, field.replace(/_id$/, ''));

  const errorHelpers = (field: string) =>
    validationErrors[field]?.[0] ?? validationErrors[field.replace(/_id$/, '')]?.[0] ?? null;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name, type, value } = event.target;
    onUserChange({ ...user, [name]: type === 'checkbox' ? checked : value });
  };

  const handleUserGroupsChange = (event: { target: { value: number[] | string } }) => {
    const { value } = event.target;
    if (!Array.isArray(value)) return;
    onUserChange({ ...user, user_group_ids: value });
  };

  const handleSupervisorChange = (id: number | null, label: string) => {
    setSupervisorLabel(label);
    onUserChange({ ...user, supervisor_id: id });
  };

  const handleSupervisor2Change = (id: number | null, label: string) => {
    setSupervisor2Label(label);
    onUserChange({ ...user, supervisor_2_id: id });
  };

  const makeFieldProps = (field: string) => ({
    error: errorStatus(field),
    helperText: errorHelpers(field),
    name: field,
    onChange: handleInputChange,
    value: user[field]
  });

  return (
    <Paper className={classes.userFormContainer}>
      <Typography variant="h6">User Information</Typography>
      <form noValidate autoComplete="off">
        <Grid container rowSpacing={3} columnSpacing={4} mb={2}>
          <Grid size={3}>
            <WaveTextField label="Employee No." {...makeFieldProps('unique_identifier')} />
          </Grid>
          <Grid size={3}>
            <WaveTextField label="Given Name" {...makeFieldProps('given_name')} />
          </Grid>
          <Grid size={3}>
            <WaveTextField label="Family Name" {...makeFieldProps('family_name')} />
          </Grid>
          <Grid size={3}>
            <WaveSelectField
              emptyMenuItem
              label="Employment Type"
              menuItems={employmentTypeFields}
              {...makeFieldProps('employment_type_id')}
            />
          </Grid>
          <Grid size={3}>
            <WaveSelectField
              emptyMenuItem
              label="Department"
              menuItems={departmentFields}
              {...makeFieldProps('department_id')}
            />
          </Grid>
          {user.is_division_manager && (
            <Grid size={3}>
              <WaveSelectField
                emptyMenuItem
                label="Division"
                menuItems={divisionFields}
                {...makeFieldProps('division_id')}
              />
            </Grid>
          )}
          {hasSupervisor && (
            <Grid size={3}>
              <Autocomplete
                error={errorStatus('supervisor_id')}
                helperText={errorHelpers('supervisor_id')}
                label="Supervisor"
                name="supervisor_id"
                onChange={handleSupervisorChange}
                role={SUPERVISOR_ROLES}
                value={supervisorLabel || ''}
              />
            </Grid>
          )}
          {hasSupervisor && (
            <Grid size={3}>
              <Autocomplete
                error={errorStatus('supervisor_2_id')}
                helperText={errorHelpers('supervisor_2_id')}
                label="Supervisor 2"
                name="supervisor_2_id"
                onChange={handleSupervisor2Change}
                role={SUPERVISOR_ROLES}
                value={supervisor2Label || ''}
              />
            </Grid>
          )}
          <Grid size={3}>
            <WaveTextField label="Email" type="email" {...makeFieldProps('email')} />
          </Grid>
          {institute.custom_user_field_1_enabled && (
            <Grid size={3}>
              <WaveTextField
                label={institute.custom_user_field_1}
                {...makeFieldProps('custom_1')}
              />
            </Grid>
          )}
          {institute.custom_user_field_2_enabled && (
            <Grid size={3}>
              <WaveTextField
                label={institute.custom_user_field_2}
                {...makeFieldProps('custom_2')}
              />
            </Grid>
          )}
          {canEditUserGroups && (
            <Grid size={3}>
              <WaveSelectField
                multiple
                label="User groups"
                menuItems={userGroupFields}
                error={errorStatus('user_group_ids')}
                helperText={errorHelpers('user_group_ids')}
                name="user_group_ids"
                onChange={handleUserGroupsChange}
                value={user.user_group_ids ?? user.user_groups?.map((group) => group.id) ?? []}
              />
            </Grid>
          )}
        </Grid>
        <Grid>
          <Grid size={12}>
            <UserRoles
              error={errorStatus('user_roles')}
              handleInputChange={handleInputChange}
              helperText={errorHelpers('user_roles')}
              user={user}
            />
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default UserForm;
